<template>
  <MobilePage>
    <div class="bg-white px-4 pt-4 border-b border-gray-200">
      <button class="flex items-center -ml-1 text-accent-primary text-sm" @click="goBack">
        <ChevronLeftIcon class="h-6 w-6" />
        <span>Companies</span>
      </button>

      <template v-if="!isLoading && company">
        <div class="mt-2 mb-3">
          <h1 class="text-2xl font-semibold text-dark-primary">{{ company.company_name }}</h1>
          <div class="mt-1 text-gray-600">{{ company.campaign ? company.campaign.campaign_name : '--' }}</div>
          <button
            class="mt-2 flex items-center gap-1 px-3 py-1 rounded-full border border-accent-primary text-sm disabled:opacity-50"
            :class="isSaved ? 'bg-accent-primary text-white' : 'text-accent-primary'"
            :disabled="isSavingCompany || (!isSaved && !isOnline)"
            @click="toggleSaved"
          >
            <BookmarkIcon v-if="isSaved" class="h-4 w-4" />
            <BookmarkOutlineIcon v-else class="h-4 w-4" />
            <span>{{ isSavingCompany ? 'Saving…' : isSaved ? 'Saved' : 'Save' }}</span>
          </button>
        </div>

        <div class="flex">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            class="flex-1 py-2 text-sm -mb-px border-b-2"
            :class="activeTab === tab.key ? 'border-accent-primary text-accent-primary font-semibold' : 'border-transparent text-gray-500'"
            @click="activeTab = tab.key"
          >{{ tab.label }}</button>
        </div>
      </template>
      <div v-else class="pb-3"></div>
    </div>

    <div class="p-4">
      <div v-if="isLoading" class="space-y-4">
        <div class="h-7 w-2/3 rounded bg-gray-200 animate-pulse"></div>
        <div class="h-4 w-1/3 rounded bg-gray-200 animate-pulse"></div>
        <div class="h-32 rounded-lg bg-gray-200 animate-pulse"></div>
        <div class="h-40 rounded-lg bg-gray-200 animate-pulse"></div>
      </div>

      <div v-else-if="!company" class="text-center text-gray-500 py-8">Couldn't load company</div>

      <template v-else>
        <div v-if="showUpdatedAt" class="-mt-2 mb-2 text-center text-xs leading-tight text-gray-500">
          Last updated {{ updatedAtText }}
        </div>

        <div v-show="activeTab === 'contacts'" class="space-y-2">
          <MobileContactPager :contacts="company.contacts" />
          <button class="w-full py-1.5 rounded-lg border border-dark-primary/30 text-sm text-dark-primary bg-white" @click="showAddContact = true">+ Add contact</button>
        </div>

        <div v-show="activeTab === 'crm'" class="space-y-4">
          <MobileCollapsibleSection title="CRM Details">
            <template v-if="canEditCRM" #title-action>
              <button class="-m-1 p-1 text-accent-primary disabled:text-gray-300" :disabled="!isOnline || !company.campaign" aria-label="Edit CRM" @click.stop="showEditCRM = true">
                <PencilSquareIcon class="h-5 w-5" />
              </button>
            </template>
            <div class="mt-2 pt-2 border-t border-gray-200">
              <div class="grid grid-cols-3 gap-x-2 gap-y-1 text-sm">
                <div class="text-gray-500">Status</div>
                <div class="col-span-2">{{ currentStatusText(company) }}</div>
                <div class="text-gray-500">Next action</div>
                <div class="col-span-2">{{ nextActionText(company) }}</div>
              </div>
              <div class="mt-2 pt-2 border-t border-gray-200">
                <div v-if="!hasCampaignDetails" class="text-sm text-gray-500">No CRM details for this campaign</div>
                <div v-else class="relative">
                  <div ref="crmListEl" class="overflow-y-auto text-sm" style="max-height: 60dvh" @scroll="updateCrmListFades">
                    <div ref="crmListContentEl">
                      <MobileCRMDetailsList :company="company" />
                    </div>
                  </div>
                  <div v-show="canScrollUp" class="pointer-events-none absolute inset-x-0 top-0 h-12 bg-gradient-to-b from-white"></div>
                  <div v-show="canScrollDown" class="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-white"></div>
                </div>
              </div>
            </div>
          </MobileCollapsibleSection>

          <MobileCollapsibleSection title="Previous Campaigns">
            <div class="mt-2">
              <div v-if="campaigns.length === 0" class="pt-2 border-t border-gray-200 text-gray-500">No campaigns</div>
              <div v-else class="border-t border-gray-200 divide-y divide-gray-200">
                <div
                  v-for="(campaign, index) in campaigns"
                  :key="campaign.id"
                  class="py-2 px-2"
                  :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-100'"
                >
                  <div>{{ campaign.campaign_name }}</div>
                  <span v-if="campaign.isCurrent" class="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-accent-primary text-white">Current</span>
                </div>
              </div>
            </div>
          </MobileCollapsibleSection>
        </div>

        <div v-show="activeTab === 'overview'" class="space-y-4">
          <MobileCollapsibleSection title="Company Details">
            <div class="mt-2 pt-2 border-t border-gray-200 space-y-2">
              <div class="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2">
                <div class="font-semibold text-gray-600">Website</div>
                <a v-if="websiteUrl" :href="websiteUrl" target="_blank" rel="noopener" class="flex items-center text-accent-primary">
                  <span class="break-all">{{ websiteLabel }}</span>
                  <ArrowTopRightOnSquareIcon class="ml-1 h-4 w-4 shrink-0" />
                </a>
                <p v-else>--</p>
                <div class="font-semibold text-gray-600">Address</div>
                <a v-if="fullAddress" :href="`https://maps.apple.com/?q=${encodeURIComponent(fullAddress)}`" target="_blank" rel="noopener" class="break-words whitespace-pre-line text-accent-primary">{{ addressLines.join('\n') }}</a>
                <p v-else>--</p>
              </div>
              <div>
                <div class="font-semibold text-gray-600 mb-1">Description</div>
                <p ref="descriptionEl" class="whitespace-pre-wrap break-words" :class="{ 'line-clamp-2': !showFullDescription }">{{ company.description || '--' }}</p>
                <button v-if="showFullDescription || isDescriptionClamped" class="mt-1 text-accent-primary" @click="showFullDescription = !showFullDescription">
                  {{ showFullDescription ? 'Show less' : 'Show more' }}
                </button>
              </div>
            </div>
          </MobileCollapsibleSection>

          <MobileCollapsibleSection title="Notes">
            <template #title-action>
              <button class="-m-1 p-1 text-accent-primary disabled:text-gray-300" :disabled="!isOnline" aria-label="Edit notes" @click.stop="showEditNotes = true">
                <PencilSquareIcon class="h-5 w-5" />
              </button>
            </template>
            <div class="mt-2 pt-2 border-t border-gray-200">
              <div class="font-semibold text-gray-600 mb-1">Account Manager Notes</div>
              <p class="whitespace-pre-wrap break-words">{{ company.account_manager_research_notes || '--' }}</p>
            </div>
            <div class="mt-2 pt-2 border-t border-gray-200">
              <span class="font-semibold text-gray-600">Client Feedback:</span> {{ clientFeedbackLabel }}
            </div>
            <div class="mt-2 pt-2 border-t border-gray-200">
              <div class="font-semibold text-gray-600 mb-1">Client Feedback Notes</div>
              <ClientFeedbackNotesList :company="company" :editable="false" />
            </div>
          </MobileCollapsibleSection>

          <div class="space-y-2">
            <button
              ref="summaryButton"
              class="w-full py-1.5 rounded-lg border border-dark-primary/30 text-sm text-dark-primary bg-white disabled:opacity-50 scroll-mt-4"
              :disabled="isGenerating || cooldownMinutesLeft > 0"
              @click="generateSummary"
            >{{ isGenerating ? 'Generating…' : aiSummary ? 'Regenerate AI Summary' : 'Generate AI Summary' }}</button>
            <div v-if="cooldownMinutesLeft > 0 && !isGenerating" class="-mt-2 -mb-1 text-[11px] text-gray-500 text-center">
              You can regenerate in {{ cooldownMinutesLeft }} min
            </div>
            <MobileCollapsibleSection v-if="aiSummary" v-model:open="showAiSummary" title="AI Summary">
              <template #subtitle>
                <div v-if="aiSummary.generated_at" class="text-xs text-gray-500">Generated {{ new Date(aiSummary.generated_at).toLocaleString() }}</div>
              </template>
              <div class="mt-2 pt-2 border-t border-gray-200 space-y-2">
                <div v-if="aiSummary.insufficient" class="text-gray-500">Not enough data</div>
                <template v-else>
                  <div>
                    <div class="font-semibold text-gray-600 mb-1">Company Details</div>
                    <p>{{ aiSummary.company_details }}</p>
                  </div>
                  <div>
                    <div class="font-semibold text-gray-600 mb-1">CRM Summary</div>
                    <p>{{ aiSummary.crm_summary }}</p>
                  </div>
                </template>
              </div>
            </MobileCollapsibleSection>
          </div>
        </div>
      </template>
    </div>
    <MobileAddContactModal
      v-if="showAddContact"
      :company="company"
      @close="showAddContact = false"
      @contact-added="onContactAdded"
    />
    <MobileEditNotesModal
      v-if="showEditNotes"
      :company="company"
      @close="showEditNotes = false"
      @notes-saved="onNotesSaved"
      @notes-changed="refreshCompany"
    />
    <MobileEditCRMModal
      v-if="showEditCRM"
      :company="company"
      @close="showEditCRM = false"
      @crm-saved="onCRMSaved"
    />
  </MobilePage>
</template>

<script setup>
import { ref, computed, defineProps, nextTick, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useResizeObserver, useOnline } from '@vueuse/core';
import { getPWACompanyByID, generateCompanyCRMSummary } from '@/services/Company';
import { currentStatusText, nextActionText } from '@/components/mobile/company/companyDisplay';
import MobileCRMDetailsList from '@/components/mobile/company/MobileCRMDetailsList.vue';
import MobilePage from '@/components/mobile/base/MobilePage.vue';
import MobileCollapsibleSection from '@/components/mobile/base/MobileCollapsibleSection.vue';
import MobileContactPager from '@/components/mobile/contact/MobileContactPager.vue';
import MobileAddContactModal from '@/components/mobile/contact/MobileAddContactModal.vue';
import MobileEditNotesModal from '@/components/mobile/company/MobileEditNotesModal.vue';
import ClientFeedbackNotesList from '@/components/company-cell-components/ClientFeedbackNotesList.vue';
import MobileEditCRMModal from '@/components/mobile/company/MobileEditCRMModal.vue';
import { useToast } from 'vue-toast-notification';
import { getSavedCompany, getSavedCompanies, saveCompany, unsaveCompany, MAX_SAVED_COMPANIES } from '@/utils/savedCompanies';
import { logSavedCompanies, NOT_LOGGED_MESSAGE } from '@/components/mobile/company/savedCompanyLog';
import { ArrowTopRightOnSquareIcon, BookmarkIcon, ChevronLeftIcon, PencilSquareIcon } from '@heroicons/vue/20/solid'
import { BookmarkIcon as BookmarkOutlineIcon } from '@heroicons/vue/24/outline'
const props = defineProps({
  companyId: String
})

const store = useStore()
const router = useRouter()

const $toast = useToast({ position: 'bottom' })

const company = ref(null)
const isLoading = ref(true)
const showAddContact = ref(false)
const showEditNotes = ref(false)
const showEditCRM = ref(false)

// Same groups as the backend's CanBulkCRM
const canEditCRM = computed(() => {
  const permissions = store.getters['auth/showUserPermissions'] || {}
  return !!(permissions.isDev || permissions.isAccountManager || permissions.isManagement || permissions.isSuperuser || permissions.isAccountStaff)
})
const isSaved = ref(false)
const isSavingCompany = ref(false)
const updatedAt = ref(null)
const usedSavedCopy = ref(false)
const isOnline = useOnline()
const showUpdatedAt = computed(() => !!updatedAt.value && (!isOnline.value || usedSavedCopy.value))
const tabs = [
  { key: 'overview', label: 'Overview' },
  { key: 'crm', label: 'CRM Details' },
  { key: 'contacts', label: 'Contacts' },
]
const activeTab = ref('overview')
const showFullDescription = ref(false)
const isDescriptionClamped = ref(false)
// Fades at the top/bottom of the CRM details list show there's more to scroll
const crmListEl = ref(null)
const crmListContentEl = ref(null)
const canScrollUp = ref(false)
const canScrollDown = ref(false)
function updateCrmListFades() {
  const el = crmListEl.value
  if (!el) {
    return
  }
  canScrollUp.value = el.scrollTop > 0
  canScrollDown.value = el.scrollTop + el.clientHeight < el.scrollHeight - 1
}
useResizeObserver(crmListEl, updateCrmListFades)
useResizeObserver(crmListContentEl, updateCrmListFades)
const descriptionEl = ref(null)
useResizeObserver(descriptionEl, () => {
  if (!showFullDescription.value) {
    isDescriptionClamped.value = descriptionEl.value.scrollHeight > descriptionEl.value.clientHeight
  }
})
const showAiSummary = ref(true)
const aiSummary = ref(null)
const isGenerating = ref(false)
const summaryButton = ref(null)
const now = ref(Date.now())
const timer = setInterval(() => { now.value = Date.now() }, 30000)
onUnmounted(() => clearInterval(timer))

const cooldownMinutesLeft = computed(() => {
  if (!aiSummary.value || !aiSummary.value.generated_at) return 0
  const elapsed = now.value - new Date(aiSummary.value.generated_at).getTime()
  return Math.min(15, Math.max(0, Math.ceil((15 * 60000 - elapsed) / 60000)))
})

const websiteUrl = computed(() => {
  const url = company.value && company.value.website_url
  if (!url) {
    return null
  }
  return url.startsWith('http') ? url : `https://${url}`
})

const websiteLabel = computed(() => {
  return company.value.website_url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '')
})

const addressLines = computed(() => {
  const stateZip = [company.value.state, company.value.zip].filter(Boolean).join(' ')
  const cityStateZip = [company.value.city, stateZip].filter(Boolean).join(', ')
  return [company.value.address, cityStateZip, company.value.country].filter(Boolean)
})

const fullAddress = computed(() => addressLines.value.join(', '))

// The latest feedback recorded for the company's current client, e.g. research_further -> Research Further
const clientFeedbackLabel = computed(() => {
  const feedback = company.value.current_client_feedback
  return feedback ? feedback.replace(/_/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase()) : '--'
})

const updatedAtText = computed(() => {
  const date = new Date(updatedAt.value)
  const day = date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' })
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  return `${day} ${time}`
})

const campaigns = computed(() => {
  const current = company.value.campaign ? [{ ...company.value.campaign, isCurrent: true }] : []
  return [...current, ...(company.value.previous_campaigns || [])]
})

const hasCampaignDetails = computed(() => {
  const campaign = company.value.campaign
  return !!campaign && (company.value.crm_actions || []).some(action => action.campaign_id == campaign.id)
})

function goBack() {
  if (window.history.state && window.history.state.back === '/pwa/companies') {
    router.back()
  } else {
    router.push({ name: 'pwa-companies' })
  }
}

const withoutTimestamps = data => JSON.stringify(data, (key, value) => key === 'loaded_at' || key === 'saved_at' ? undefined : value)

// Shows the list's copy (or the saved copy) right away, then refreshes it in the background
async function loadCompany() {
  const state = window.history.state || {}
  const listCompany = state.company && state.company.id == props.companyId ? state.company : null
  const savedCompany = await getSavedCompany(props.companyId)
  isSaved.value = !!savedCompany
  if (listCompany || savedCompany) {
    showInitialCompany(listCompany || savedCompany)
  }
  await refreshCompany()
  isLoading.value = false
}

function showInitialCompany(initialCompany) {
  company.value = initialCompany
  aiSummary.value = initialCompany.ai_summary
  updatedAt.value = initialCompany.loaded_at || initialCompany.saved_at
  usedSavedCopy.value = !initialCompany.loaded_at
  isLoading.value = false
}

// Updates this company's history entry, so a refresh or back/forward shows the latest
function updateHistoryState(changes) {
  const route = router.currentRoute.value
  if (route.name === 'pwa-company-detail' && route.params.companyId == props.companyId) {
    window.history.replaceState({ ...window.history.state, ...changes }, '')
  }
}

// Writes the latest company to this history entry and, if saved, to the saved copy
function persistCompany(latestCompany) {
  updateHistoryState({ company: { ...latestCompany, loaded_at: updatedAt.value } })
  if (isSaved.value) {
    saveCompany(latestCompany).catch(error => console.log(error))
  }
}

// Only the latest refresh is applied, so an older response can't overwrite newer data
let latestRefreshId = 0

async function refreshCompany() {
  const refreshId = ++latestRefreshId
  const result = await getPWACompanyByID({ token: store.getters['auth/showToken'], companyId: props.companyId, timeout: 3500 })
  if (refreshId !== latestRefreshId || !(result && result.response && result.response.status === 200)) {
    return
  }
  const freshCompany = result.response.data
  // Only replace when something changed, so the page (e.g. the contact pager) doesn't reset
  if (!company.value || withoutTimestamps(freshCompany) !== withoutTimestamps(company.value)) {
    company.value = freshCompany
    if (!isGenerating.value) {
      aiSummary.value = freshCompany.ai_summary
    }
  }
  updatedAt.value = new Date().toISOString()
  usedSavedCopy.value = false
  persistCompany(freshCompany)
}

async function toggleSaved() {
  isSavingCompany.value = true
  try {
    if (isSaved.value) {
      await unsaveCompany(company.value.id)
      isSaved.value = false
    } else if ((await getSavedCompanies()).length >= MAX_SAVED_COMPANIES) {
      $toast.open({ message: `You can only save up to ${MAX_SAVED_COMPANIES} companies`, type: 'error', duration: 2000 })
    } else if (!(await logSavedCompanies([company.value.id], store.getters['auth/showToken']))) {
      $toast.open({ message: NOT_LOGGED_MESSAGE, type: 'error', duration: 2000 })
    } else {
      await saveCompany(company.value)
      isSaved.value = true
    }
  } catch (error) {
    console.log(error)
    $toast.open({ message: "Couldn't update saved companies", type: 'error', duration: 2000 })
  } finally {
    isSavingCompany.value = false
  }
}

async function generateSummary() {
  if (aiSummary.value && !confirm('Regenerate the AI summary? This will replace the current summary.')) return
  isGenerating.value = true
  try {
    const data = await generateCompanyCRMSummary(props.companyId, store.getters['auth/showToken'])
    if (data && data.summary) {
      // A refresh started before this would bring back the old summary, so drop it
      latestRefreshId++
      aiSummary.value = data.summary
      company.value.ai_summary = data.summary
      persistCompany(JSON.parse(JSON.stringify(company.value)))
      showAiSummary.value = true
      now.value = Date.now()
      await nextTick()
      summaryButton.value.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } else {
      $toast.open({ message: "Couldn't generate summary", type: 'error', duration: 2000 })
    }
  } catch (error) {
    const message = error.response && error.response.status === 429
      ? error.response.data.detail
      : "Couldn't generate summary"
    $toast.open({ message, type: 'error', duration: 2000 })
  } finally {
    isGenerating.value = false
  }
}

async function onContactAdded() {
  showAddContact.value = false
  $toast.open({ message: `Contact added to ${company.value.company_name}`, type: 'success', duration: 2000 })
  await refreshCompany()
}

async function onNotesSaved(changes) {
  showEditNotes.value = false
  Object.assign(company.value, changes)
  persistCompany(JSON.parse(JSON.stringify(company.value)))
  $toast.open({ message: 'Notes saved', type: 'success', duration: 2000 })
  await refreshCompany()
}

async function onCRMSaved(failed) {
  if (failed.length === 0) {
    showEditCRM.value = false
    $toast.open({ message: 'CRM updated', type: 'success', duration: 2000 })
  }
  await refreshCompany()
}

loadCompany()
</script>
